import { MetricFilterSet, MetricKey } from "@/services/metrics";
import { ResourceChannel } from "@/services/resources";
import { computed, ComputedRef, reactive, ref, Ref } from "vue";
import api from "@/services/api";
import {
  useCalculatedMetricKeys,
  MetricType,
} from "@/services/calculated_metrics";
import {
  TemporalDataFilter,
  ResourcesDatafilter,
  TimeUnit,
  TagsDataFilter,
  AdNetworkFilter,
  PKFilter,
  DateRangeSelection,
  ConversionFilter,
  TemporalPeriodsType,
} from "@/services/data_filters";
import {
  useConversions,
  useMetricFilter,
  useDetachedTemporalFilter,
} from "@/store/data_filters";
import {
  useDebouncedDataFilters,
  useDebouncedCalculatedResources,
  useDebouncedConversionGroups,
  useDebouncedDetachedTagFilter,
} from "@/store/debounced_filters";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/vue-query";
import {
  AggregatedMarketingMetrics,
  MarketingEntityRunningStatus,
  MarketingMetric,
  ComparisonMarketingMetrics,
  MarketingMetrics,
} from "@/services/marketing";
import { useResetPage } from "@/services/utils";
import { ResponseTimeSeriesDatasets } from "@/utils/datasets";
import { QueryFunctionContext } from "@/utils/typing";
import { usePublicDashboardWidgetQueryFilters } from "./public_dashboard";
import { filterCriteriaFormatter } from "@/utils/filterCriteriaFormatter";
import { AscendingOrDescending } from "@/components/DataPresentation";
import { getGoogleAdsHeadlines, isGoogleAdHeaderArray } from "@/utils/ads";

export enum AdContentType {
  // SoMe FB, LinkedIn, Snapchat
  IMAGE = "image",
  MULTI_IMAGE = "multiple_images",
  VIDEO = "video",
  CAROUSEL = "carousel",
  DYNAMIC_IMAGE = "dynamic_image",
  DYNAMIC_VIDEO = "dynamic_video",

  // Google search
  RSA = "RSA",
  ETA = "ETA",
  SHOPPING = "shopping",
  // TEXT = "text", // unsupported

  // Google discovery ads
  DISCOVERY_MULTI_ASSET_AD = "discovery_multi_asset",
  DISCOVERY_CAROUSEL_AD = "discovery_carousel",

  // Google/YouTube video ads
  RESPONSIVE_VIDEO = "responsive_video",
  BUMPER_VIDEO = "bumper_video",
  OUTSTREAM_VIDEO = "outstream_video",
  NON_SKIPPABLE_IN_STREAM_VIDEO = "non_skippable_in_stream_video",
  SKIPPABLE_IN_STREAM_VIDEO = "skippable_in_stream_video",
  IN_FEED_VIDEO = "in_feed_video",
  UNKNOWN = "unknown",
}
export interface AdWithContentType extends BaseAd {
  contentType: AdContentType;
}

export enum EditAdContentType {
  GOOGLE_RSA = "GOOGLE_RSA",
}

export enum GoogleAdsAdStrength {
  PENDING = "PENDING",
  POOR = "POOR",
  AVERAGE = "AVERAGE",
  GOOD = "GOOD",
  EXCELLENT = "EXCELLENT",
}

export interface BaseAd extends MarketingMetrics, ComparisonMarketingMetrics {
  pk: number;
  name: string;
  externalId: string;
  resource: number;
  campaign: number;
  adGroup: number;
  channel: ResourceChannel;
  // "" = status unknown (channel without status integration / not re-cached)
  status: MarketingEntityRunningStatus | "" | null;
  rawStatus?: string;
  statusSyncedAt?: string | null;
  // Own status doesn't reflect parent pauses on every channel (Google).
  adGroupStatus?: MarketingEntityRunningStatus | "" | null;
  campaignStatus?: MarketingEntityRunningStatus | "" | null;
  metricsByDate?: Record<
    TemporalPeriodsType,
    Record<MarketingMetric, ResponseTimeSeriesDatasets>
  >;
  tags: number[];
  socialMediaPage: number | null; // null for Google Search Ads
  googleAdsAdStrength: GoogleAdsAdStrength | ""; // blank for unknown strengths and other channels
  // networks: AdNetwork[]
  // availableActions: AdStatusAction[];
  hasCampaignBudget: boolean;
  hasAdGroupBudget: boolean;
}

export interface SocialMediaPage {
  pk: number;
  name: string;
  imageSrc: string;
  url: string;
}

export interface GoogleAdHeadline {
  text: string;
  pinned: 1 | 2 | 3 | null;
}

export interface GoogleAdDescription {
  text: string;
  pinned: 1 | 2 | null;
}

export type GoogleAdsAd = Omit<BaseAd, "channel"> & {
  channel: ResourceChannel.GOOGLE_ADS;
};

export type BingAdsAd = Omit<BaseAd, "channel"> & {
  channel: ResourceChannel.BING_ADS;
};

export interface SearchAdUrlContent {
  url: string | null;
  displayUrl: string;
  path1?: string;
  path2?: string;
}

export interface SocialMediaAdBaseContent {
  text: string;
  title?: string;
  description?: string;
  destination?: string;
  callToAction?: string;
  caption?: string;
  url?: string; // snapchat uses url instead of destination
  headline?: string; // snapchat uses headline instead of title
  brandName?: string; // snapchat uses brandname instead of caption
}
export interface SocialMediaDynamicAdBaseContent {
  texts: string[];
  titles?: string[];
  descriptions?: string[];
  destinations?: string[];
  callToActions?: string[];
  captions?: string[];
}

export interface SocialMediaImageContent extends SocialMediaAdBaseContent {
  imageUrl: string;
}
export interface SocialMediaVideoContent extends SocialMediaImageContent {
  videoUrl?: string;
  videoThumbnailUrl?: string;
}
export interface SocialMediaDynamicImageContent extends SocialMediaDynamicAdBaseContent {
  imageUrls: string[];
}
export interface SocialMediaDynamicVideoContent extends SocialMediaDynamicImageContent {
  videoUrls?: string[];
  videoThumbnailUrls?: string[];
}
export type SocialMediaTextOnlyContent = SocialMediaAdBaseContent;
export interface SocialMediaCarouselContent {
  cards: (SocialMediaImageContent | SocialMediaVideoContent)[];
}

export interface SearchTextAdContent<
  T1 = string,
  T2 = string,
> extends SearchAdUrlContent {
  headline1: T1;
  description1: T2;
  description2: T2;
}

export interface SearchEtaContent<
  T1 = string,
  T2 = string,
> extends SearchTextAdContent<T1, T2> {
  headline2: T1;
  headline3: T1;
}
export interface SearchRsaContent extends SearchEtaContent<
  GoogleAdHeadline,
  GoogleAdDescription
> {
  headline4?: GoogleAdHeadline;
  headline5?: GoogleAdHeadline;
  headline6?: GoogleAdHeadline;
  headline7?: GoogleAdHeadline;
  headline8?: GoogleAdHeadline;
  headline9?: GoogleAdHeadline;
  headline10?: GoogleAdHeadline;
  headline11?: GoogleAdHeadline;
  headline12?: GoogleAdHeadline;
  headline13?: GoogleAdHeadline;
  headline14?: GoogleAdHeadline;
  headline15?: GoogleAdHeadline;
  description3?: GoogleAdDescription;
  description4?: GoogleAdDescription;
}
export interface SocialMediaVideoAd extends BaseAd {
  contentType: AdContentType.VIDEO;
  content: SocialMediaVideoContent;
}
export interface SocialMediaImageAd extends BaseAd {
  contentType: AdContentType.IMAGE;
  content: SocialMediaImageContent;
}
export interface SocialMediaDynamicImageAd extends BaseAd {
  contentType: AdContentType.DYNAMIC_IMAGE;
  content: SocialMediaDynamicImageContent;
}
export interface SocialMediaDynamicVideoAd extends BaseAd {
  contentType: AdContentType.DYNAMIC_VIDEO;
  content: SocialMediaDynamicVideoContent;
}

export interface SocialMediaCarouselAd extends BaseAd {
  contentType: AdContentType.CAROUSEL;
  content: SocialMediaCarouselContent;
}

export interface GoogleSearchRsa extends GoogleAdsAd {
  contentType: AdContentType.RSA;
  content: SearchRsaContent;
}
export interface GoogleSearchEta extends GoogleAdsAd {
  contentType: AdContentType.ETA;
  content: SearchEtaContent;
}
export interface GoogleShoppingAd extends GoogleAdsAd {
  contentType: AdContentType.SHOPPING;
  content: Record<string, never>;
  product: {
    image: string;
    name: string;
    price: number | null;
    salePrice: number | null;
    categories: string;
  };
}

export interface GoogleBaseVideoAdContent extends SearchAdUrlContent {
  videoId: string;
}
export interface GoogleResponsiveVideoAdContent extends GoogleBaseVideoAdContent {
  callToAction: string | null;
  longHeadline: string;
  description: string;
  headline: string | null;
}

export type GoogleBumperVideoAdContent = GoogleBaseVideoAdContent;

export interface GoogleOutstreamVideoAdContent extends GoogleBaseVideoAdContent {
  headline: string;
  description: string;
}

export interface GoogleNonSkippableInstreamVideoAdContent extends GoogleBaseVideoAdContent {
  headline: string;
  callToAction: string;
}

export type GoogleSkippableInStreamVideoAdContent =
  GoogleNonSkippableInstreamVideoAdContent;

export interface GoogleInFeedVideoAdContent extends GoogleBaseVideoAdContent {
  headline: string;
  description1: string;
  description2: string;
}

export interface GoogleResponsiveVideoAd extends GoogleAdsAd {
  contentType: AdContentType.RESPONSIVE_VIDEO;
  content: GoogleResponsiveVideoAdContent;
}
export interface GoogleBumperVideoAd extends GoogleAdsAd {
  contentType: AdContentType.BUMPER_VIDEO;
  content: GoogleBumperVideoAdContent;
}
export interface GoogleOutstreamVideoAd extends GoogleAdsAd {
  contentType: AdContentType.OUTSTREAM_VIDEO;
  content: GoogleOutstreamVideoAdContent;
}
export interface GoogleNonSkippableInstreamVideoAd extends GoogleAdsAd {
  contentType: AdContentType.NON_SKIPPABLE_IN_STREAM_VIDEO;
  content: GoogleNonSkippableInstreamVideoAdContent;
}
export interface GoogleSkippableInStreamVideoAd extends GoogleAdsAd {
  contentType: AdContentType.SKIPPABLE_IN_STREAM_VIDEO;
  content: GoogleSkippableInStreamVideoAdContent;
}
export interface GoogleInFeedVideoAd extends GoogleAdsAd {
  contentType: AdContentType.IN_FEED_VIDEO;
  content: GoogleInFeedVideoAdContent;
}

export interface GoogleDiscoveryMultiAssetAdContent extends SearchAdUrlContent {
  // Arrays indicate different options, but only one is shown at a time.
  headlines: string[];
  descriptions: string[];
  businessName: string;
  callToAction: string;
  logoImages: string[];
  images: string[];
}

export interface GoogleDiscoveryMultiAssetAd extends GoogleAdsAd {
  contentType: AdContentType.DISCOVERY_MULTI_ASSET_AD;
  content: GoogleDiscoveryMultiAssetAdContent;
}

export interface GoogleDiscoveryCarouselAdContent extends SearchAdUrlContent {
  headline: string;
  description: string;
  businessName: string;
  logoImage: string;
  cards: {
    headline: string;
    callToAction: string;
    // Arrays indicate different options, but only one is shown at a time.
    images: string[];
  }[];
}

export interface GoogleDiscoveryCarouselAd extends GoogleAdsAd {
  contentType: AdContentType.DISCOVERY_CAROUSEL_AD;
  content: GoogleDiscoveryCarouselAdContent;
}

export type GenericGoogleVideoAd =
  | GoogleResponsiveVideoAd
  | GoogleBumperVideoAd
  | GoogleOutstreamVideoAd
  | GoogleNonSkippableInstreamVideoAd
  | GoogleSkippableInStreamVideoAd
  | GoogleInFeedVideoAd;

export type GenericGoogleSearchAd =
  | GoogleSearchRsa
  | GoogleSearchEta
  | GoogleShoppingAd;

export interface BingSearchRsa extends BingAdsAd {
  contentType: AdContentType.RSA;
  content: SearchRsaContent;
}
export interface BingSearchEta extends BingAdsAd {
  contentType: AdContentType.ETA;
  content: SearchEtaContent;
}

export type GenericBingSearchAd = BingSearchRsa | BingSearchEta;

export type GenericSocialMediaAd =
  | SocialMediaVideoAd
  | SocialMediaImageAd
  | SocialMediaDynamicImageAd
  | SocialMediaDynamicVideoAd
  | SocialMediaCarouselAd;

export interface AdWithoutPreview extends BaseAd {
  contentType: "";
  content: Record<string, never>;
}

export type Ad =
  | GenericSocialMediaAd
  | GenericGoogleSearchAd
  | GenericGoogleVideoAd
  | GoogleDiscoveryMultiAssetAd
  | GoogleDiscoveryCarouselAd
  | GenericBingSearchAd
  | AdWithoutPreview;

// Type assertion functions
export function isAd(media: object): media is Ad {
  // adGroup is unique to the ad hierarchy (campaign → ad group → ad)
  // and won't appear on Post or CompetitorPost
  return "adGroup" in media && "contentType" in media;
}

// Google search
export function isGoogleSearchEta(ad: Ad): ad is GoogleSearchEta {
  return (
    ad.channel === ResourceChannel.GOOGLE_ADS &&
    ad.contentType === AdContentType.ETA
  );
}
export function isGoogleSearchRsa(ad: Ad): ad is GoogleSearchRsa {
  return (
    ad.channel === ResourceChannel.GOOGLE_ADS &&
    ad.contentType === AdContentType.RSA
  );
}
export function isGoogleSearchAd(ad: Ad): ad is GenericGoogleSearchAd {
  return isGoogleSearchEta(ad) || isGoogleSearchRsa(ad);
}

export function isBingSearchEta(ad: Ad): ad is BingSearchEta {
  return (
    ad.channel === ResourceChannel.BING_ADS &&
    ad.contentType === AdContentType.ETA
  );
}
export function isBingSearchRsa(ad: Ad): ad is BingSearchRsa {
  return (
    ad.channel === ResourceChannel.BING_ADS &&
    ad.contentType === AdContentType.RSA
  );
}
export function isBingSearchAd(ad: Ad): ad is GenericBingSearchAd {
  return isBingSearchEta(ad) || isBingSearchRsa(ad);
}

// Social media
export function isSocialMediaAd(ad: Ad): ad is GenericSocialMediaAd {
  return [
    ResourceChannel.FACEBOOK_ADS,
    ResourceChannel.LINKED_IN_ADS,
    ResourceChannel.SNAPCHAT_ADS,
    ResourceChannel.TIK_TOK_ADS,
  ].includes(ad.channel);
}
export function isSocialMediaSingleImageAd(ad: Ad): ad is SocialMediaImageAd {
  return isSocialMediaAd(ad) && ad.contentType === AdContentType.IMAGE;
}
export function isSocialMediaCarouselAd(ad: Ad): ad is SocialMediaCarouselAd {
  return isSocialMediaAd(ad) && ad.contentType === AdContentType.CAROUSEL;
}
export function isSocialMediaVideoAd(ad: Ad): ad is SocialMediaVideoAd {
  return isSocialMediaAd(ad) && ad.contentType === AdContentType.VIDEO;
}
export function isSocialMediaDynamicImageAd(
  ad: Ad,
): ad is SocialMediaDynamicImageAd {
  return isSocialMediaAd(ad) && ad.contentType === AdContentType.DYNAMIC_IMAGE;
}
export function isSocialMediaDynamicVideoAd(
  ad: Ad,
): ad is SocialMediaDynamicVideoAd {
  return isSocialMediaAd(ad) && ad.contentType === AdContentType.DYNAMIC_VIDEO;
}

// Google video
export function isGoogleVideoAd(ad: Ad): ad is GenericGoogleVideoAd {
  return "videoId" in ad.content && ad.channel === ResourceChannel.GOOGLE_ADS;
}
export function isGoogleResponsiveVideoAd(
  ad: Ad,
): ad is GoogleResponsiveVideoAd {
  return (
    isGoogleVideoAd(ad) && ad.contentType === AdContentType.RESPONSIVE_VIDEO
  );
}
export function isGoogleBumperVideoAd(ad: Ad): ad is GoogleBumperVideoAd {
  return isGoogleVideoAd(ad) && ad.contentType === AdContentType.BUMPER_VIDEO;
}
export function isGoogleSkippableInStreamVideoAd(
  ad: Ad,
): ad is GoogleSkippableInStreamVideoAd {
  return (
    isGoogleVideoAd(ad) &&
    ad.contentType === AdContentType.SKIPPABLE_IN_STREAM_VIDEO
  );
}
export function isGoogleNonSkippableInstreamVideoAd(
  ad: Ad,
): ad is GoogleNonSkippableInstreamVideoAd {
  return (
    isGoogleVideoAd(ad) &&
    ad.contentType === AdContentType.NON_SKIPPABLE_IN_STREAM_VIDEO
  );
}
export function isGoogleOutstreamVideoAd(ad: Ad): ad is GoogleOutstreamVideoAd {
  return (
    isGoogleVideoAd(ad) && ad.contentType === AdContentType.OUTSTREAM_VIDEO
  );
}
export function isGoogleInFeedVideoAd(ad: Ad): ad is GoogleInFeedVideoAd {
  return isGoogleVideoAd(ad) && ad.contentType === AdContentType.IN_FEED_VIDEO;
}

export function isGoogleShoppingAd(ad: Ad): ad is GoogleShoppingAd {
  return "product" in ad && ad.product !== null;
}

export function isGoogleDiscoveryMultiAssetAd(
  ad: Ad,
): ad is GoogleDiscoveryMultiAssetAd {
  return (
    ad.channel === ResourceChannel.GOOGLE_ADS &&
    ad.contentType === AdContentType.DISCOVERY_MULTI_ASSET_AD
  );
}

export function isGoogleDiscoveryCarouselAd(
  ad: Ad,
): ad is GoogleDiscoveryCarouselAd {
  return (
    ad.channel === ResourceChannel.GOOGLE_ADS &&
    ad.contentType === AdContentType.DISCOVERY_CAROUSEL_AD
  );
}

export interface AdResponse {
  // null on page 2+; MarketingPagination skips COUNT(*) there
  count: number | null;
  next: string | null;
  previous: string | null;
  results: Ad[];
  pageTotals?: AggregatedMarketingMetrics;
  overallTotals?: AggregatedMarketingMetrics;
}

export type AdOrdering = "name" | MarketingMetric;

type AdsParams = {
  page?: number;
  ordering: AscendingOrDescending<AdOrdering>[];
  search: string;
  pageSize: number;
  includeTotals: boolean;
  includeMetricsByDate: boolean;
  adGroup: number | undefined;
  campaign: number | undefined;
  timeUnit: TimeUnit;
  filterCriteria?: MetricFilterSet[];
  serving?: boolean;
} & ResourcesDatafilter &
  TemporalDataFilter &
  TagsDataFilter &
  AdNetworkFilter &
  ConversionFilter &
  PKFilter;

function listAds({
  queryKey: [, params],
  ...context
}:
  | QueryFunctionContext<AdsParams, number | undefined>
  | QueryFunctionContext<AdsParams>): Promise<AdResponse> {
  const filterCriteria = filterCriteriaFormatter(params.filterCriteria);
  return api
    .get("/resources/marketing/ads/", {
      params: {
        page: "pageParam" in context ? context.pageParam : params.page,
        ...params,
        ...filterCriteria,
      },
    })
    .then((response) => {
      function transformCTA(cta: string): string {
        const transformations: Record<string, string> = {
          BOOK_TRAVEL: "BOOK_NOW",
        };
        return cta in transformations ? transformations[cta] : cta;
      }
      function contentHasCTA(content: object): boolean {
        return ["callToAction", "callToActions"].some((property) =>
          Object.hasOwn(content, property),
        );
      }

      const data = response.data;
      data.results = data.results.map((result: Ad) => {
        // Map CTA transformations
        if (contentHasCTA(result.content)) {
          const transformedContent = result.content as Record<
            string,
            string[] | string
          >;

          if (Object.hasOwn(transformedContent, "callToActions")) {
            transformedContent.callToActions = (
              transformedContent.callToActions as string[]
            ).map(transformCTA);
          }
          if (Object.hasOwn(transformedContent, "callToAction")) {
            transformedContent.callToAction = transformCTA(
              transformedContent.callToAction as string,
            );
          }
          return {
            ...result,
            content: {
              ...result.content,
              ...transformedContent,
            },
          };
        }

        return result;
      });
      return data;
    });
}

export const BASE_AD_METRICS = [
  "cost",
  "impressions",
  "clicks",
  "totalClicks",
  "conversions",
  "value",
  "conversionsByConvDate",
  "valueByConvDate",
  "returnOnAdSpend",
  "returnOnAdSpendRatio",
  "returnOnAdSpendByConvDate",
  "returnOnAdSpendRatioByConvDate",
  "costPerConversion",
  "costPerClick",
  "clickthroughRate",
  "costPerMilli",
  "shares",
  "engagement",
  "comments",
  "reach",
  "frequency",
  "initialVideoViews",
  "videoViews25Pct",
  "videoViews50Pct",
  "videoViews75Pct",
  "videoViews100Pct",
  "metaThruplays",
  "youtubeViews",
  "averageVideoWatchTime",
  "costPerInitialVideoView",
  "costPerMetaThruplay",
  "youtubeViewRate",
  "totalVideoWatchTime",
  "outboundClicks",
  "videoViews15s",
  "videoViews6s",
  "landingPageViews",
  "storyOpens",
  "costPerVideoViews15s",
  "costPerVideoViews6s",
] as const satisfies MetricKey[];

export function useMarketingPostMetrics(): ComputedRef<MarketingMetric[]> {
  const calculatedMetricKeys = useCalculatedMetricKeys(
    MetricType.MARKETING,
    BASE_AD_METRICS,
  );
  return computed(() => [...BASE_AD_METRICS, ...calculatedMetricKeys.value]);
}

export function useAdsQuery({
  page,
  ordering,
  search,
  pageSize,
  includeTotals,
  includeMetricsByDate,
  adGroup,
  campaign,
  timeUnit = ref(TimeUnit.DAY),
  pks,
  enabled,
  filterCriteria,
  selectedResourceIds,
  conversionGroups,
  conversions,
  includedTags,
  excludedTags,
  includedTagsLogic,
  temporalFilter,
  useGlobalFilters = true,
  metrics,
  serving,
}: {
  page: Ref<number>;
  ordering: Ref<AscendingOrDescending<AdOrdering>[]>;
  search: Ref<string>;
  pageSize: Ref<number>;
  includeTotals: Ref<boolean>;
  includeMetricsByDate: Ref<boolean>;
  adGroup: Ref<number | undefined>;
  campaign?: Ref<number | undefined>;
  timeUnit: Ref<TimeUnit>;
  pks?: Ref<number[]>;
  enabled?: Ref<boolean>;
  filterCriteria?: Ref<MetricFilterSet[]>;
  selectedResourceIds?: Ref<number[]>;
  conversionGroups?: Ref<number[]>;
  conversions?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  temporalFilter?: Ref<DateRangeSelection>;
  useGlobalFilters?: boolean;
  metrics?: Ref<string[]>;
  serving?: Ref<boolean | undefined>;
}) {
  const queryKey = [
    "ads",
    {
      page,
      ordering,
      search,
      pageSize,
      includeTotals,
      includeMetricsByDate,
      campaign,
      adGroup,
      timeUnit,
      pks,
      filterCriteria,
      serving,
      ...useDebouncedDataFilters(
        ["subcategory", "conversionGroup", "conversion"],
        useGlobalFilters,
      ),
      ...useDebouncedCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDebouncedDetachedTagFilter(
        useGlobalFilters,
        includedTags,
        excludedTags,
        includedTagsLogic,
      ),
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
      ...usePublicDashboardWidgetQueryFilters(),
      ...useDebouncedConversionGroups(conversionGroups, useGlobalFilters),
      ...useConversions(conversions, useGlobalFilters),
      ...useMetricFilter(metrics, useGlobalFilters),
    },
  ] as const;
  useResetPage(page, queryKey, [
    "timeUnit",
    "includeTotals",
    "includeMetricsByDate",
  ]);
  return reactive(
    useQuery({
      queryKey,
      queryFn: listAds,
      enabled: computed(() => pageSize.value > 0 && (enabled?.value ?? true)),
      placeholderData: (previousData) =>
        page.value > 1 ? previousData : undefined,
    }),
  );
}

export const useAdsQueryWithMetrics = {
  query: useAdsQuery,
  metrics: BASE_AD_METRICS,
};

export function useAdsInfiniteQuery({
  ordering,
  search,
  pageSize,
  includeTotals,
  includeMetricsByDate,
  adGroup,
  campaign,
  enabled,
  timeUnit = ref(TimeUnit.DAY),
  selectedResourceIds,
  includedTags,
  excludedTags,
  includedTagsLogic,
  temporalFilter,
  useGlobalFilters = true,
}: {
  ordering: Ref<AscendingOrDescending<AdOrdering>[]>;
  search: Ref<string>;
  pageSize: Ref<number>;
  includeTotals: Ref<boolean>;
  includeMetricsByDate: Ref<boolean>;
  adGroup: Ref<number | undefined>;
  campaign?: Ref<number | undefined>;
  enabled?: Ref<boolean>;
  timeUnit: Ref<TimeUnit>;
  selectedResourceIds?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  temporalFilter?: Ref<DateRangeSelection>;
  useGlobalFilters?: boolean;
}) {
  const queryKey = [
    "infiniteAds",
    {
      ordering,
      search,
      pageSize,
      includeTotals,
      includeMetricsByDate,
      campaign,
      adGroup,
      timeUnit,
      ...useDebouncedDataFilters(
        ["subcategory", "conversionGroup", "conversion"],
        useGlobalFilters,
      ),
      ...useDebouncedCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDebouncedDetachedTagFilter(
        useGlobalFilters,
        includedTags,
        excludedTags,
        includedTagsLogic,
      ),
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
    },
  ] as const;
  return reactive(
    useInfiniteQuery({
      queryKey,
      queryFn: listAds,
      enabled,
      placeholderData: keepPreviousData,
      initialPageParam: undefined,
      getNextPageParam: (lastPage, pages) =>
        lastPage.next ? pages.length + 1 : undefined,
    }),
  );
}

function listSocialMediaPages({
  queryKey: [, { accountId }],
}: QueryFunctionContext<{ accountId: number }>): Promise<SocialMediaPage[]> {
  return api
    .get(`/resources/marketing/accounts/${accountId}/social_media_pages/`)
    .then((response) => response.data);
}

export function useSocialMediaPagesQuery({
  accountId,
}: {
  accountId: Ref<number>;
}) {
  const queryKey = ["socialMediaPages", { accountId }] as const;
  return reactive(useQuery({ queryKey, queryFn: listSocialMediaPages }));
}
export function getAdType(ad: AdWithContentType): string {
  if (!ad || !ad.contentType) return "Unknown";

  switch (ad.channel) {
    case ResourceChannel.GOOGLE_ADS:
      switch (ad.contentType) {
        case AdContentType.RSA:
          return "Google Responsive Search Ad";
        case AdContentType.ETA:
          return "Google Expanded Text Ad";
        case AdContentType.SHOPPING:
          return "Google Shopping Ad";
        case AdContentType.BUMPER_VIDEO:
          return "Google Bumper Video Ad";
        case AdContentType.SKIPPABLE_IN_STREAM_VIDEO:
          return "Google Skippable In-Stream Video Ad";
        case AdContentType.NON_SKIPPABLE_IN_STREAM_VIDEO:
          return "Google Non-Skippable In-Stream Video Ad";
        case AdContentType.DISCOVERY_MULTI_ASSET_AD:
          return "Google Discovery Multi-Asset Ad";
        case AdContentType.DISCOVERY_CAROUSEL_AD:
          return "Google Discovery Carousel Ad";
        default:
          return "Unknown Google Ad";
      }

    case ResourceChannel.BING_ADS:
      switch (ad.contentType) {
        case AdContentType.RSA:
          return "Bing Responsive Search Ad";
        case AdContentType.ETA:
          return "Bing Expanded Text Ad";
        default:
          return "Unknown Bing Ad";
      }

    case ResourceChannel.FACEBOOK_ADS:
    case ResourceChannel.FACEBOOK_ORGANIC:
    case ResourceChannel.LINKED_IN_ADS:
    case ResourceChannel.LINKED_IN_ORGANIC:
    case ResourceChannel.SNAPCHAT_ADS:
    case ResourceChannel.TIK_TOK_ADS:
    case ResourceChannel.TIK_TOK_ORGANIC:
      switch (ad.contentType) {
        case AdContentType.IMAGE:
          return "Social Media Image Ad";
        case AdContentType.VIDEO:
          return "Social Media Video Ad";
        case AdContentType.CAROUSEL:
          return "Social Media Carousel Ad";
        case AdContentType.DYNAMIC_IMAGE:
          return "Social Media Dynamic Image Ad";
        case AdContentType.DYNAMIC_VIDEO:
          return "Social Media Dynamic Video Ad";
        default:
          return "Unknown Social Media Ad";
      }

    default:
      return "Unknown Ad Type";
  }
}
export function getAdTypeIconName(ad: Ad) {
  if (ad.contentType.includes("video")) {
    return "video";
  } else if (ad.contentType === "image") {
    return "image";
  } else if (ad.contentType === "dynamic_image") {
    return "images";
  } else {
    return "ad-solid";
  }
}

export function getAdTitle(ad: Ad) {
  if (isSocialMediaDynamicImageAd(ad) || isSocialMediaDynamicVideoAd(ad)) {
    return ad.content?.titles?.find((title) => title !== undefined);
  } else if (isSocialMediaVideoAd(ad) || isSocialMediaSingleImageAd(ad)) {
    return ad.content?.title;
  } else if (isSocialMediaCarouselAd(ad)) {
    return ad.content.cards[0]?.title;
  } else if (isGoogleVideoAd(ad)) {
    return "headline" in ad.content ? (ad.content.headline ?? "") : "";
  } else if (isGoogleSearchAd(ad) || isBingSearchAd(ad)) {
    const headlines = Object.entries(ad.content)
      .map(([key, val]) => {
        if (key.includes("headline")) {
          return val;
        }
      })
      .filter(Boolean);
    if (isGoogleAdHeaderArray(headlines)) {
      const sortedHeadlines = getGoogleAdsHeadlines(headlines, 2);
      return sortedHeadlines[0].text + " - " + sortedHeadlines[1].text;
    } else return headlines[0] + " - " + headlines[1];
  } else if (isGoogleDiscoveryMultiAssetAd(ad)) {
    return ad.content.headlines[0];
  } else {
    return "";
  }
}

export function getSearchAdContent(
  content: SearchRsaContent | SearchEtaContent | Record<string, never>,
) {
  const headlines = Object.entries(content).map(([key, val]) => {
    if (key.includes("headline")) {
      return val;
    }
  });
  const descriptions = Object.entries(content).map(([key, val]) => {
    if (key.includes("description")) {
      return val;
    }
  });
  return {
    headlines: headlines.filter(Boolean) as string[] | GoogleAdHeadline[],
    descriptions: descriptions.filter(Boolean) as
      | string[]
      | GoogleAdDescription[],
    url: content.url,
  };
}
